import { Drawer, DataGrid } from '@core/ui';
import { Download, X, Table, FileText, CheckCircle2 } from 'lucide-react';
import React from 'react';

/**
 * ExportPreview - Industrial verification layer before CSV generation.
 * Shows a grid of the data to be exported and provides the final commit action.
 */
const EMPTY_ARRAY = [];

const ExportPreview = ({
    isOpen,
    onClose,
    onConfirm,
    data = EMPTY_ARRAY,
    columns = EMPTY_ARRAY,
    title = "Export Preview",
    filename = "export.csv"
}) => {
    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            subtitle={`Verifying ${data.length} records for ${filename}`}
            icon={Table}
            width="max-w-4xl"
        >
            <div className="flex flex-col h-full gap-6">
                {/* Visual Confirmation Banner */}
                <div className="flex items-center gap-4 p-5 bg-emerald-50 border border-emerald-100 rounded-3xl animate-in fade-in slide-in-from-top-2">
                    <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-emerald-500 shadow-sm border border-emerald-100">
                        <CheckCircle2 size={24} strokeWidth={2.5} />
                    </div>
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest leading-none mb-1">Data Ready</span>
                        <h4 className="font-black text-primary text-base">Verify records before download</h4>
                    </div>
                </div>

                {/* Data Grid Preview */}
                <div className="flex-1 min-h-0 border border-slate-100 rounded-[2rem] overflow-hidden bg-slate-50/30">
                    <DataGrid
                        data={data.slice(0, 100)} // Show up to 100 for preview performance
                        columns={columns}
                        loading={false}
                        stickyOffset="0px"
                        emptyTitle="No Data"
                        emptyDescription="No records match your current filters."
                    />
                </div>

                {/* Footer Actions */}
                <div className="flex items-center gap-4 pt-4 mt-auto border-t border-slate-100 bg-white/50 backdrop-blur-sm sticky bottom-0">
                    <button
                        onClick={onClose}
                        className="flex-1 py-4 bg-slate-100 text-slate-400 font-black rounded-2xl hover:bg-slate-200 hover:text-slate-600 transition-all active:scale-95 uppercase tracking-widest text-[10px]"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl shadow-xl shadow-emerald-200 hover:bg-emerald-700 transition-all active:scale-95 flex items-center justify-center gap-3 uppercase tracking-[0.2em] text-[10px]"
                    >
                        <Download size={18} strokeWidth={2.5} />
                        Confirm & Download CSV
                    </button>
                </div>

                {data.length > 100 && (
                    <p className="text-[10px] text-slate-400 font-bold text-center italic">
                        Viewing first 100 of {data.length} total records...
                    </p>
                )}
            </div>
        </Drawer>
    );
};

export default React.memo(ExportPreview);
